import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      
      // Navigate based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'team_manager') {
        navigate('/team-manager');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Form - Mobile optimized */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8 sm:mb-12">
            <img 
              src="/images/gigseasy-logo.svg" 
              alt="GigsEasy Logo" 
              className="h-10"
            />
          </Link>

          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Welcome back</h1>
          <p className="text-slate-600 mb-6 sm:mb-8 text-sm sm:text-base">Sign in to continue earning rewards</p>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 sm:pl-12 h-11 sm:h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl text-base"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 sm:pl-12 h-11 sm:h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl text-base"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 sm:h-12 bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white font-medium rounded-full shadow-lg shadow-[#00B894]/25 hover:opacity-90 hover:-translate-y-0.5 transition-all"
            >
              {loading ? "Signing in..." : "Sign In"}
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </form>

          <div className="relative my-6 sm:my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-slate-500">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full h-11 sm:h-12 border-2 border-slate-200 hover:border-[#00B894] rounded-full transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-slate-600 mt-6 sm:mt-8 text-sm sm:text-base">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#00B894] font-medium hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </p>
          <p className="text-center mt-3">
            <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-[#00B894] hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image (hidden on mobile) */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-[#00B894] to-[#0984E3]">
        <div className="h-full w-full relative flex items-center justify-center p-12">
          <div className="text-center">
            <img 
              src="/images/hero-image.png" 
              alt="GigsEasy Platform"
              className="w-full max-w-lg mx-auto mb-8 drop-shadow-2xl"
            />
            <blockquote className="text-white">
              <p className="text-xl font-medium mb-4">
                "I've been using GigsEasy for 6 months and it's been a great way to earn extra income in my spare time."
              </p>
              <footer className="text-white/80">
                - Sarah M., Top Earner
              </footer>
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
}
